/**
 * Role utility helpers for the new 42-role system.
 * Centralises all role category checks so components don't hardcode role arrays.
 */

/** Check if a role belongs to the professional team (external consultants) */
export const isProfessionalRole = (role) => {
  if (!role) return false;
  // New prof_ prefix or legacy aliases
  return role.startsWith('prof_') || 
    ['professional_team', 'consultant', 'principal_agent'].includes(role);
};

/** Check if a role is a management-level internal role */
export const isManagementRole = (role) => {
  if (!role) return false;
  return ['admin', 'director', 'project_director', 'project_manager', 'contracts_director'].includes(role);
};

/** Check if a role is a commercial/planning internal role */
export const isCommercialRole = (role) => {
  if (!role) return false;
  return ['contracts_manager', 'commercial_manager', 'quantity_surveyor', 'planner', 'cm', 'qs'].includes(role);
};

/** Check if a role is a site delivery internal role */
export const isSiteRole = (role) => {
  if (!role) return false;
  return ['construction_manager', 'site_agent', 'site_manager', 'site_engineer', 'foreman'].includes(role);
};

/** Check if a role can approve delay events (management + site seniors) */
export const canApproveEvents = (role) => {
  if (!role) return false;
  return ['admin', 'director', 'project_director', 'project_manager',
    'contracts_manager', 'commercial_manager', 'construction_manager',
    'site_agent', 'site_manager', 'site_engineer',
    'contracts_director'].includes(role);
};

/** Human-readable label for any role key (including legacy aliases) */
export const getRoleLabel = (role) => {
  if (!role) return '';
  return ROLE_DISPLAY_NAMES[role] || role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/** Comprehensive role display name map */
export const ROLE_DISPLAY_NAMES = {
  admin: 'Administrator',
  // Internal: Management
  director: 'Director',
  project_director: 'Project Director',
  project_manager: 'Project Manager',
  // Internal: Commercial / Planning
  contracts_manager: 'Contracts Manager',
  commercial_manager: 'Commercial Manager',
  quantity_surveyor: 'Quantity Surveyor',
  planner: 'Planner',
  // Internal: Site Delivery
  construction_manager: 'Construction Manager',
  site_agent: 'Site Agent',
  site_manager: 'Site Manager',
  site_engineer: 'Site Engineer',
  foreman: 'Foreman',
  // Internal: Support
  procurement_manager: 'Procurement Manager',
  plant_manager: 'Plant Manager',
  stores_manager: 'Stores Manager',
  document_controller: 'Document Controller',
  quality_manager: 'Quality Manager',
  safety_officer: 'Safety Officer',
  project_administrator: 'Project Administrator',
  other: 'Other',
  // Professional Team
  prof_principal_agent: 'Principal Agent',
  prof_project_manager: 'Project Manager (Professional)',
  prof_architect: 'Architect',
  prof_architectural_technologist: 'Architectural Technologist',
  prof_quantity_surveyor: 'Quantity Surveyor (Professional)',
  prof_civil_engineer: 'Civil Engineer',
  prof_structural_engineer: 'Structural Engineer',
  prof_electrical_engineer: 'Electrical Engineer',
  prof_mechanical_engineer: 'Mechanical Engineer',
  prof_wet_services_engineer: 'Wet Services Engineer',
  prof_fire_engineer: 'Fire Engineer',
  prof_electronic_ict_engineer: 'Electronic / ICT Engineer',
  prof_interior_designer: 'Interior Designer',
  prof_landscape_architect: 'Landscape Architect',
  prof_town_planner: 'Town Planner',
  prof_traffic_engineer: 'Traffic Engineer',
  prof_geotechnical_engineer: 'Geotechnical Engineer',
  prof_environmental_consultant: 'Environmental Consultant',
  prof_health_safety_consultant: 'Health and Safety Consultant',
  prof_clerk_of_works: 'Clerk of Works',
  prof_other: 'Other (Professional)',
  // Legacy aliases
  qs: 'Quantity Surveyor',
  cm: 'Contracts Manager',
  contracts_director: 'Director',
  professional_team: 'Professional Team',
  consultant: 'Consultant',
  pm: 'Project Manager',
  hs_officer: 'Safety Officer',
  project_admin: 'Project Administrator',
  client_representative: 'Client Representative',
  contractor: 'Contractor',
};

/** Badge color classes for role display */
export const getRoleBadgeColor = (role) => {
  if (!role) return 'bg-slate-100 text-slate-700 border-slate-200';
  if (role === 'admin') return 'bg-red-100 text-red-700 border-red-200';
  if (isManagementRole(role)) return 'bg-purple-100 text-purple-700 border-purple-200';
  if (isCommercialRole(role)) return 'bg-blue-100 text-blue-700 border-blue-200';
  if (isSiteRole(role)) return 'bg-green-100 text-green-700 border-green-200';
  if (isProfessionalRole(role)) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};
